import classNames from 'classnames'
import React, {
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { AccessibleDropdownList, useModalPortalContainer } from '../../atoms'
import { getPositionStyles } from '../../atoms/form-date/dateUtils'
import { FormInput, Props as FormInputProps } from '../../atoms/form-input'

export interface FormInputSearchProps extends Omit<FormInputProps, 'list' | 'onChange'> {
  list: ReactNode[]
  internalSearch?: boolean
  onChange?: (value: string) => void
  onItemSelect?: (selectedIndex: number) => void
}

const getStringValue = (value: ReactNode): string =>
  React.isValidElement<{ children?: ReactNode }>(value) && value.props.children
    ? String(value.props.children)
    : String(value || '')

export const FormInputSearch = ({
  list,
  onChange,
  internalSearch = true,
  className,
  onItemSelect,
  ...props
}: FormInputSearchProps) => {
  const [open, setOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const listRef = useRef<HTMLUListElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({ opacity: 0 })
  const portalContainer = useModalPortalContainer()

  const dropdownListId = useId()

  const filteredList = useMemo(() => {
    return list
      .map((value, index) => ({ value, index, label: value, code: index }))
      .filter(({ value }) => {
        const stringValue = getStringValue(value)
        return internalSearch ? stringValue.toLocaleLowerCase().includes(`${props.value}`.toLocaleLowerCase()) : true
      })
  }, [props.value, list, internalSearch])

  useEffect(() => {
    setSelectedIndex(0)
  }, [props.value])

  const isOpen = useMemo(() => open && filteredList.length > 0, [open, filteredList.length])

  const close = useCallback(() => {
    setOpen(false)
    setSelectedIndex(0)
  }, [])

  const closeIfApplicable = useCallback(
    (event: MouseEvent) => {
      const path = event.composedPath()
      if (
        listRef.current &&
        !path.includes(listRef.current) &&
        (!inputRef.current || !path.includes(inputRef.current))
      ) {
        close()
      }
    },
    [close]
  )

  useEffect(() => {
    window.addEventListener('click', closeIfApplicable)
    return () => {
      window.removeEventListener('click', closeIfApplicable)
    }
  }, [closeIfApplicable])

  // biome-ignore lint/correctness/useExhaustiveDependencies: isOpen is needed, otherwise else the list will not open in a second field using this component
  useEffect(() => {
    const inputRect = inputRef?.current?.getBoundingClientRect()
    if (listRef.current && inputRect) {
      const bodyRect = document.body.getBoundingClientRect()
      const popperRect = listRef.current.getBoundingClientRect()
      setMenuStyles({ ...getPositionStyles(bodyRect, inputRect, popperRect), width: inputRect.width })
    }
  }, [isOpen])

  const handleSelect = (code?: string | number) => {
    onItemSelect ? onItemSelect(code as number) : onChange?.(getStringValue(list[code as number]))
    close()
  }

  const onInputChange = (event: FormEvent<HTMLInputElement>) => {
    onChange?.((event.target as HTMLInputElement).value)
    setSelectedIndex(0)
    setOpen(true)
  }

  const handleKeyStroke = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && !open) {
      setOpen(true)
    }
    if (event.key === 'Escape' && isOpen) {
      close()
    }

    const currentIndex = filteredList.findIndex((item) => item.index === selectedIndex)

    if (event.key === 'ArrowUp' && selectedIndex > 0) {
      const newIndex = filteredList.at(currentIndex - 1)?.index || 0

      setSelectedIndex(newIndex)
    } else if (event.key === 'ArrowDown' && selectedIndex < list.length - 1) {
      const newIndex = filteredList.at(currentIndex + 1)?.index
      const fallbackIndex = filteredList.at(0)?.index || 0

      setSelectedIndex(newIndex || fallbackIndex)
    } else if (event.key === 'Enter' && isOpen) {
      event.preventDefault()
      handleSelect(selectedIndex)
    }
    props.onKeyDown?.(event)
  }

  return (
    <div className={classNames('relative', className)}>
      <FormInput
        {...props}
        ref={inputRef}
        onKeyDown={handleKeyStroke}
        onChange={onInputChange}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        aria-controls={dropdownListId}
        aria-expanded={isOpen}
      />
      {createPortal(
        <AccessibleDropdownList
          options={filteredList}
          id={dropdownListId}
          isOpen={isOpen}
          onClose={close}
          onSelect={handleSelect}
          renderInPortal
          searchable={false}
          alignMenu="left"
          style={menuStyles}
          inputRef={inputRef}
          listRef={listRef}
          selectedIndex={selectedIndex}
        />,
        portalContainer
      )}
    </div>
  )
}
